/**
 * Web Router
 * React Router configuration for web/PWA
 */

import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { View, Text, ActivityIndicator, StyleSheet } from 'react-native';

// Web Bottom Navigation
import WebBottomNav from '../components/WebBottomNav';

// Import screens directly - they're already optimized
import LoginScreen from '../screens/LoginScreen';
import RegisterScreen from '../screens/RegisterScreen';
import EmailVerificationScreen from '../screens/EmailVerificationScreen';
import SubscriptionScreen from '../screens/SubscriptionScreen';
import ForgotPasswordScreen from '../screens/ForgotPasswordScreen';
import HomeScreen from '../screens/HomeScreen';
import ChartsScreen from '../screens/ChartsScreen';
import TradesScreen from '../screens/TradesScreen';
import HistoryScreen from '../screens/HistoryScreen';
import SettingScreen from '../screens/SettingScreen';
import WalletScreen from '../screens/WalletScreen';
import ReferralScreen from '../screens/ReferralScreen';
import WithdrawalReferralScreen from '../screens/WithdrawalReferralScreen';
import DepositScreen from '../screens/DepositScreen';
import WithdrawalScreen from '../screens/WithdrawalScreen';
import EditProfileScreen from '../screens/EditProfileScreen';
import ChangePasswordScreen from '../screens/ChangePasswordScreen';
import HelpSupportScreen from '../screens/HelpSupportScreen';
import TermsScreen from '../screens/TermsScreen';
import PrivacyScreen from '../screens/PrivacyScreen';
import AboutScreen from '../screens/AboutScreen';

// Web Navigation Adapter
import { useWebNavigation } from './WebNavigationAdapter';

// HOC to inject navigation props into screens
const withNavigation = (Component) => {
  return (props) => {
    const { navigation, route } = useWebNavigation();
    return <Component {...props} navigation={navigation} route={route} />;
  };
};

// Wrap all screens with navigation
const Login = withNavigation(LoginScreen);
const Register = withNavigation(RegisterScreen);
const EmailVerification = withNavigation(EmailVerificationScreen);
const Subscription = withNavigation(SubscriptionScreen);
const ForgotPassword = withNavigation(ForgotPasswordScreen);
const Home = withNavigation(HomeScreen);
const Charts = withNavigation(ChartsScreen);
const Trades = withNavigation(TradesScreen);
const History = withNavigation(HistoryScreen);
const Setting = withNavigation(SettingScreen);
const Wallet = withNavigation(WalletScreen);
const Referral = withNavigation(ReferralScreen);
const WithdrawalReferral = withNavigation(WithdrawalReferralScreen);
const Deposit = withNavigation(DepositScreen);
const Withdrawal = withNavigation(WithdrawalScreen);
const EditProfile = withNavigation(EditProfileScreen);
const ChangePassword = withNavigation(ChangePasswordScreen);
const HelpSupport = withNavigation(HelpSupportScreen);
const Terms = withNavigation(TermsScreen);
const Privacy = withNavigation(PrivacyScreen);
const About = withNavigation(AboutScreen);

// Loading component
const LoadingScreen = () => (
  <View style={styles.loading}>
    <ActivityIndicator size="large" color="#00f5a0" />
    <Text style={styles.loadingText}>Loading...</Text>
  </View>
);

// Layout wrapper with bottom nav
const AppLayout = ({ children }) => {
  const location = useLocation();
  
  // Routes where bottom nav should NOT appear
  const hideNavRoutes = ['/', '/register', '/email-verification', '/forgot-password', '/subscription'];
  const shouldShowNav = !hideNavRoutes.includes(location.pathname);

  return (
    <View style={styles.appContainer}>
      <View style={styles.content}>
        {children}
      </View>
      {shouldShowNav && <WebBottomNav />}
    </View>
  );
};

const WebRouter = () => {
  return (
    <BrowserRouter>
      <AppLayout>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Auth Routes */}
            <Route path="/" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/email-verification" element={<EmailVerification />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* Main App Routes */}
            <Route path="/home" element={<Home />} />
            <Route path="/charts" element={<Charts />} />
            <Route path="/trades" element={<Trades />} />
            <Route path="/history" element={<History />} />
            
            {/* Profile & Settings */}
            <Route path="/settings" element={<Setting />} />
            <Route path="/profile" element={<Setting />} />
            <Route path="/edit-profile" element={<EditProfile />} />
            <Route path="/change-password" element={<ChangePassword />} />
            
            {/* Wallet & Transactions */}
            <Route path="/wallet" element={<Wallet />} />
            <Route path="/deposit" element={<Deposit />} />
            <Route path="/withdrawal" element={<Withdrawal />} />
            
            {/* Referral */}
            <Route path="/referral" element={<Referral />} />
            <Route path="/withdrawal-referral" element={<WithdrawalReferral />} />
            
            {/* Info Pages */}
            <Route path="/help" element={<HelpSupport />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<About />} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AppLayout>
    </BrowserRouter>
  );
};

const styles = StyleSheet.create({
  appContainer: {
    flex: 1,
    backgroundColor: '#0B1121',
  },
  content: {
    flex: 1,
    marginBottom: 70, // Space for bottom nav (increased)
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0B1121',
  },
  loadingText: {
    color: '#00f5a0',
    marginTop: 10,
    fontSize: 16,
    fontWeight: '500',
  },
});

export default WebRouter;
